// Session lifecycle for the SPA (FedRAMP AC-11/AC-12): short access tokens renewed with a
// rotating refresh token, proactive renewal before expiry, idle logout shared across tabs,
// and server-side revocation on logout. The stored "user" object is the single source of
// truth for tokens; components learn about rotations through the "session:updated" event.

import { API_BASE_URL } from "../config";
import { cognitoLogoutUrl } from "./cognitoAuth";

const DEFAULT_IDLE_SECONDS = 30 * 60; // server sends idle_timeout_seconds; this is the fallback
const REFRESH_LEAD_MS = 60 * 1000; // renew this long before the access token expires
const TICK_MS = 30 * 1000;
const ACTIVITY_EVENTS = ["mousemove", "keydown", "click", "scroll", "touchstart"];
const ACTIVITY_KEY = "session:lastActivity"; // shared by every tab of this browser
const LOCK_NAME = "session:refresh";

const storage = () => (localStorage.getItem("user") ? localStorage : sessionStorage);

export const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || sessionStorage.getItem("user") || "null");
  } catch {
    return null;
  }
};

export const clearUser = () => {
  localStorage.removeItem("user");
  sessionStorage.removeItem("user");
};

// The server answers 403 "password_change_required" to a first-login token on every route but
// change-password; the app must not be entered until the issued password is changed.
export const PASSWORD_CHANGE_REQUIRED = "password_change_required";
export const needsPasswordChange = (user) => Boolean(user?.isfirstlogin);

export const requirePasswordChange = () => {
  const user = readUser();
  if (user && !user.isfirstlogin) {
    storage().setItem("user", JSON.stringify({ ...user, isfirstlogin: true }));
  }
  if (!window.location.pathname?.startsWith("/login")) {
    window.location.href = "/login";
  }
};

// Merge freshly issued tokens into the stored user and tell React about it.
export const storeTokens = (data) => {
  const user = readUser();
  if (!user || !data?.token) {
    return user;
  }
  const next = {
    ...user,
    token: data.token,
    refresh_token: data.refresh_token ?? user.refresh_token,
    expires_in: data.expires_in ?? user.expires_in,
    idle_timeout_seconds: data.idle_timeout_seconds ?? user.idle_timeout_seconds,
    tokenIssuedAt: Date.now(),
  };
  storage().setItem("user", JSON.stringify(next));
  window.dispatchEvent(new CustomEvent("session:updated", { detail: next }));
  return next;
};

const postRefresh = async (refreshToken) => {
  const r = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
  });
  return r.ok ? r.json() : null;
};

let inflight = null;

// Returns the updated user, null when the server rejected the session (log out), or false on a
// network failure (leave the session alone and try again later). One refresh at a time per tab,
// and one per browser via the Web Locks API where available, so two tabs never present the
// same refresh token (the server treats a replay as a leak and ends the whole session).
export const refreshSession = () => {
  if (inflight) {
    return inflight;
  }
  const run = async () => {
    const user = readUser();
    if (!user?.refresh_token) {
      return null;
    }
    try {
      const data = await postRefresh(user.refresh_token);
      return data ? storeTokens(data) : null;
    } catch {
      return false;
    }
  };
  const locked = async () => {
    if (navigator.locks?.request) {
      return navigator.locks.request(LOCK_NAME, async () => {
        // another tab may have rotated the token while we waited for the lock
        const before = readUser();
        if (before?.tokenIssuedAt && Date.now() - before.tokenIssuedAt < REFRESH_LEAD_MS) {
          return before;
        }
        return run();
      });
    }
    return run();
  };
  inflight = locked().finally(() => {
    inflight = null;
  });
  return inflight;
};

// Forget the session locally first (so a closed tab cannot resurrect it), then revoke it.
// SSO sessions also end the Cognito managed-login session, else a fresh SSO click would
// silently re-authenticate without a credential prompt.
export const endSession = () => {
  const user = readUser();
  const refreshToken = user?.refresh_token;
  const ssoUsed = user?.ssoUsed;
  clearUser();
  const redirectAfterLogout = () => {
    if (ssoUsed) {
      window.location.assign(cognitoLogoutUrl());
    }
  };
  if (!refreshToken) {
    redirectAfterLogout();
    return Promise.resolve();
  }
  return fetch(`${API_BASE_URL}/api/auth/logout`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ refresh_token: refreshToken }),
    keepalive: true,
  })
    .catch(() => undefined) // the server expires it on its own
    .finally(redirectAfterLogout);
};

// Shared 401 handling for every API module: refresh once and retry, log out only when the
// server rejects the session, leave everything alone on a network failure.
export const retryAfterUnauthorized = async (retry) => {
  const renewed = await refreshSession();
  if (renewed) {
    return retry();
  }
  if (renewed === null) {
    clearUser();
    window.location.href = "/login";
    throw new Error("Session expired. Please login again.");
  }
  throw new Error("Could not reach the server to renew the session. Please retry.");
};

const lastActivity = () => Number(localStorage.getItem(ACTIVITY_KEY) || 0);

// Proactive token renewal and idle logout. Returns a stop function.
export const startSessionTimers = (onExpired) => {
  let lastWrite = 0;
  const touch = () => {
    const now = Date.now();
    if (now - lastWrite > 5000) {
      lastWrite = now;
      localStorage.setItem(ACTIVITY_KEY, String(now));
    }
  };
  touch();
  ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, touch, { passive: true }));

  const tick = async () => {
    const user = readUser();
    if (!user?.refresh_token) {
      return;
    }
    const idleMs = (user.idle_timeout_seconds || DEFAULT_IDLE_SECONDS) * 1000;
    if (Date.now() - lastActivity() > idleMs) {
      await endSession();
      onExpired("idle");
      return;
    }
    const ttlMs = (user.expires_in || DEFAULT_IDLE_SECONDS) * 1000;
    if (Date.now() - (user.tokenIssuedAt || 0) > ttlMs - REFRESH_LEAD_MS) {
      const renewed = await refreshSession();
      if (renewed === null) {
        clearUser();
        onExpired("expired");
      }
    }
  };
  const interval = setInterval(tick, TICK_MS);
  return () => {
    clearInterval(interval);
    ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, touch));
  };
};
